import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Sparkles, Search,
    ScanLine, ArrowLeftRight, Database, Wand2, Recycle, LayoutTemplate,
    FileSpreadsheet, Tags, ScanText, FileSignature, Truck, ClipboardList,
    FileStack, Layers, Stamp, ShieldCheck, TrendingUp, LifeBuoy,
} from "lucide-react";
import { api } from "@/lib/api";
import { EDI_MODULES_BY_KEY } from "@/lib/edi/modules";
import { EDI } from "@/constants/testIds";
import BaselineMyProject from "@/components/edi/BaselineMyProject";
import BoardPackGenerator from "@/components/edi/BoardPackGenerator";
import KnowledgeLifecycle from "@/components/edi/KnowledgeLifecycle";
import SharePointSync from "@/components/edi/SharePointSync";
import DataSheetIntelligence from "@/components/edi/DataSheetIntelligence";
import PlantBaselineGenerator from "@/components/edi/PlantBaselineGenerator";
import DigitalEngineeringTwin from "@/components/edi/DigitalEngineeringTwin";

const ICONS = {
    ScanLine, ArrowLeftRight, Database, Wand2, Recycle, LayoutTemplate,
    FileSpreadsheet, Tags, ScanText, FileSignature, Truck, ClipboardList,
    FileStack, Layers, Stamp, ShieldCheck, TrendingUp, LifeBuoy,
};

const SEARCH_MODULES = new Set([
    "repository",
    "eng_knowledge_repo",
    "ocr",
    "reuse",
    "enterprise_search",
]);

export default function ModuleView() {
    const { key } = useParams();
    const module = EDI_MODULES_BY_KEY[key];
    const [aiOutput, setAiOutput] = useState("");
    const [aiBusy, setAiBusy] = useState(false);
    const [customPrompt, setCustomPrompt] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [searchHits, setSearchHits] = useState(null);

    if (!module) return <Navigate to="/edi" replace />;

    const Icon = ICONS[module.icon] || Sparkles;

    const runAi = async (prompt) => {
        if (!prompt || aiBusy) return;
        setAiBusy(true);
        setAiOutput("");
        try {
            const { data } = await api.post("/edi/ai", {
                module_key: module.key,
                prompt,
                context: `${module.name} — ${module.purpose}`,
            });
            setAiOutput(data.result);
            toast.success("AI insight ready");
        } catch (e) {
            toast.error("AI request failed");
        } finally {
            setAiBusy(false);
        }
    };

    const runSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery.trim()) return;
        try {
            const { data } = await api.post("/edi/search", { query: searchQuery });
            setSearchHits(data.hits);
        } catch {
            toast.error("Search failed");
        }
    };

    return (
        <div className="space-y-6" data-testid={EDI.moduleRoot}>
            <Link to="/edi" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-[var(--sgsb-gold)]">
                <ArrowLeft className="h-3 w-3" /> Back to Enterprise Delivery Intelligence™
            </Link>

            {/* Header */}
            <div className="glass rounded-xl p-6 relative overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.18), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.14), transparent 55%)",
                    }}
                />
                <div className="relative flex items-start gap-5">
                    <div className="h-14 w-14 rounded-lg border border-[color:var(--sgsb-gold)]/40 bg-[color:var(--sgsb-gold)]/10 grid place-items-center shrink-0">
                        <Icon className="h-7 w-7 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    </div>
                    <div className="min-w-0">
                        <div className="kicker text-slate-500 text-[10px]">{module.section}</div>
                        <h1 className="mt-1 font-display font-black text-white text-3xl md:text-4xl tracking-tighter leading-[1.05]">
                            {module.name}
                        </h1>
                        <p className="mt-2 text-slate-300 font-body text-sm leading-relaxed max-w-3xl">
                            {module.tagline}
                        </p>
                        <p className="mt-2 text-slate-400 font-body text-sm leading-relaxed max-w-3xl">
                            {module.purpose}
                        </p>
                    </div>
                </div>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {module.kpis.map((kpi) => (
                    <div key={kpi.label} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                        <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                        <div className="kicker text-slate-400 text-[10px]">{kpi.label}</div>
                        <div className="mt-3 font-display font-black text-white text-2xl tracking-tighter">{kpi.value}</div>
                    </div>
                ))}
            </div>

            {/* Special interactive workspaces */}
            {module.key === "ai_reuse_advisor" && <BaselineMyProject />}
            {module.key === "board_pack_generator" && <BoardPackGenerator />}
            {module.key === "knowledge_lifecycle" && <KnowledgeLifecycle />}
            {module.key === "sharepoint" && <SharePointSync />}
            {module.key === "data_sheet_intelligence" && <DataSheetIntelligence />}
            {module.key === "plant_baseline_generator" && <PlantBaselineGenerator />}
            {module.key === "digital_twin" && <DigitalEngineeringTwin />}

            {/* Search (Repository, Engineering Repo, OCR, Reuse and Enterprise Search modules get a proper searchbar) */}
            {SEARCH_MODULES.has(module.key) && (
                <div className="glass rounded-xl p-5">
                    <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                        <Search className="h-4 w-4" strokeWidth={1.5} />
                        <span className="kicker text-[10px]">Enterprise Search</span>
                    </div>
                    <form onSubmit={runSearch} className="mt-3 flex gap-2">
                        <input
                            data-testid={EDI.searchInput}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={`Search e.g. "Rip Tank", "Gold CIP", "Harmony Gold mechanical spec"…`}
                            className="flex-1 rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 placeholder:text-slate-500 outline-none focus:border-[color:var(--sgsb-gold)]"
                        />
                        <button
                            type="submit"
                            data-testid={EDI.searchSubmit}
                            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-4 py-2 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold-soft)] transition-colors duration-200"
                        >
                            <Search className="h-3.5 w-3.5" strokeWidth={2} /> Search
                        </button>
                    </form>
                    {searchHits && (
                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full text-sm font-body">
                                <thead>
                                    <tr className="text-slate-500 kicker text-[10px]">
                                        <th className="text-left px-3 py-2">Ref</th>
                                        <th className="text-left px-3 py-2">Type</th>
                                        <th className="text-left px-3 py-2">Project</th>
                                        <th className="text-left px-3 py-2">Rev</th>
                                        <th className="text-right px-3 py-2">Similarity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {searchHits.length === 0 && (
                                        <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500">No matches. Try a different term.</td></tr>
                                    )}
                                    {searchHits.map((h) => (
                                        <tr key={h.ref} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                            <td className="px-3 py-2 text-slate-100">{h.ref}</td>
                                            <td className="px-3 py-2 text-slate-300">{h.type}</td>
                                            <td className="px-3 py-2 text-slate-300">{h.project}</td>
                                            <td className="px-3 py-2 text-slate-400">{h.revision}</td>
                                            <td className="px-3 py-2 text-right text-[var(--sgsb-gold)] font-display font-bold">{(h.similarity * 100).toFixed(0)}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Data table */}
            <div className="glass rounded-xl overflow-hidden">
                <div className="p-4 border-b border-slate-800/60 flex items-center justify-between">
                    <h3 className="font-display font-black text-white tracking-tight">{module.table.title}</h3>
                    <span className="kicker text-slate-500 text-[10px]">{module.table.rows.length} records</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm font-body">
                        <thead>
                            <tr className="text-slate-500 kicker text-[10px]">
                                {module.table.columns.map((c) => (
                                    <th key={c} className="text-left px-4 py-3">{c}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {module.table.rows.map((r, i) => (
                                <tr key={i} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                    {r.map((c, j) => (
                                        <td key={j} className="px-4 py-2.5 text-slate-200">{c}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* AI Prompts */}
            <div className="glass rounded-xl p-5">
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">AI Advisor · GPT-5.2</span>
                </div>
                <h3 className="mt-1 font-display font-black text-white text-lg tracking-tight">
                    Ask the {module.name.replace("™", "")} advisor
                </h3>
                <div className="mt-3 flex flex-wrap gap-2">
                    {module.aiPrompts.map((p, i) => (
                        <button
                            key={i}
                            data-testid={EDI.aiPrompt(module.key, i)}
                            disabled={aiBusy}
                            onClick={() => runAi(p)}
                            className="text-xs font-body px-3 py-1.5 rounded-full bg-slate-950/70 border border-slate-800 text-slate-300 hover:text-[var(--sgsb-gold)] hover:border-[color:var(--sgsb-gold)]/50 transition-colors duration-200 disabled:opacity-60"
                        >
                            {p}
                        </button>
                    ))}
                </div>
                <form
                    onSubmit={(e) => { e.preventDefault(); runAi(customPrompt); setCustomPrompt(""); }}
                    className="mt-4 flex gap-2"
                >
                    <input
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="Ask a custom question about this capability…"
                        className="flex-1 rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 placeholder:text-slate-500 outline-none focus:border-[color:var(--sgsb-gold)]"
                        data-testid={EDI.aiCustomInput}
                    />
                    <button
                        type="submit"
                        disabled={aiBusy || !customPrompt.trim()}
                        data-testid={EDI.aiCustomSubmit}
                        className="inline-flex items-center gap-1.5 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-4 py-2 font-display font-bold text-xs hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                    >
                        <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
                        {aiBusy ? "Thinking…" : "Ask"}
                    </button>
                </form>

                {aiOutput && (
                    <div className="mt-4 rounded-md bg-slate-950/60 border border-slate-800 p-4">
                        <div className="kicker text-[var(--sgsb-gold)] text-[10px] mb-2">AI response</div>
                        <pre className="whitespace-pre-wrap text-slate-200 font-body text-sm leading-relaxed">{aiOutput}</pre>
                    </div>
                )}
            </div>
        </div>
    );
}
